import { ErrorHandler, inject, InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { ApplicationContextPropertiesSet, GuestService, HostService } from '@vendasta/meetings';
import { combineLatest, Observable, of, SchedulerLike } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, timeout } from 'rxjs/operators';
import { MeetingSchedulerServiceModule } from './data-providers/meeting-scheduler-service.module';
import { MeetingSchedulerStoreService } from './data-providers/meeting-scheduler-store.service';
import {
  ALTERNATE_TRANSLATIONS_TOKEN,
  APPLICATION_CONTEXT_TOKEN,
  CALENDAR_PAGE_LINK_TOKEN,
  CALENDAR_SLUG_TOKEN,
  CONTEXT_DATA_TOKEN,
  ContextData,
  EVENT_TYPE_PAGE_TOKEN,
  GROUP_PAGE_TOKEN,
  GUEST_SERVICE_TOKEN,
  HOST_ID_TOKEN,
  HOST_SERVICE_TOKEN,
  ICONS_TOKEN,
  INITIAL_AVAILABILITY_HOURS,
  INITIAL_AVAILABILITY_HOURS_TIMEOUT,
  INTEGRATIONS_PAGE_LINK_TOKEN,
  MEETING_INTEGRATION_SERVICE_KEYS_TOKEN,
  MEETING_LIST_PAGE_TOKEN,
  MEETING_TYPE_LIST_VIEW_PAGE_TOKEN,
  MEETING_SCHEDULER_CALENDAR_SERVICE_TOKEN,
  MEETING_SCHEDULER_MEETING_NAVIGATION_SERVICE_TOKEN,
  MeetingNavigationLinkService,
  MeetingSchedulerCalendarService,
  PERSONAL_CALENDAR_ID_TOKEN,
  RECOMMENDED_GROUP_CALENDAR_SLUGS_TOKEN,
  RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN,
  SETTINGS_LINK_TOKEN,
  StubMeetingNavigationLinkService,
  StubMeetingSchedulerCalendarService,
  USER_ID_TOKEN,
  SERVICE_PAGE_TOKEN,
  CALENDAR_VIEW_PAGE_LINK_TOKEN,
  FEATURE_MEETING_DATE_RANGE_TOKEN,
  FEATURE_MEETING_SESSION_EVENT_TOKEN,
  FEATURE_PRIORITY_ASSIGNMENT_TOKEN,
  CONTACT_PROFILE_PAGE_TOKEN, MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN, FEATURE_SMS_ENABLED_TOKEN,CRM_ACTIVITY_FEED_PAGE_TOKEN,
  Context,
} from './data-providers/providers';
import {
  DailyAvailability,
  MeetingIntegrationServiceKeys,
  MeetingSchedulerConfig,
  MeetingSchedulerIcons,
  MeetingSchedulerNavigationLinks,
} from './interface';
import { getDefaultAvailability, getObservableString } from './utils';

export const MEETING_SCHEDULER_CONFIG_TOKEN = new InjectionToken('[Meeting Scheduler]: Internal token for config');

@NgModule({
  declarations: [],
  imports: [],
  providers: [],
})
export class MeetingSchedulerDataModule {
  static forRoot(c: {
    config: InjectionToken<MeetingSchedulerConfig>;
  }): ModuleWithProviders<MeetingSchedulerServiceModule> {
    const result = {
      ngModule: MeetingSchedulerServiceModule,
      providers: [
        GuestService,
        HostService,
        { provide: MEETING_SCHEDULER_CONFIG_TOKEN, useExisting: c.config },
        { provide: USER_ID_TOKEN, deps: [MEETING_SCHEDULER_CONFIG_TOKEN], useFactory: userIdFactory },
        { provide: HOST_ID_TOKEN, deps: [MEETING_SCHEDULER_CONFIG_TOKEN], useFactory: hostIdFactory },
        { provide: CALENDAR_SLUG_TOKEN, deps: [MeetingSchedulerStoreService], useFactory: personalCalendarSlugFactory },
        { provide: GUEST_SERVICE_TOKEN, useExisting: GuestService },
        { provide: HOST_SERVICE_TOKEN, useExisting: HostService },
        {
          provide: MEETING_SCHEDULER_CALENDAR_SERVICE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: calendarServiceFactory,
        },
        {
          provide: RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: recommendedPersonalCalendarSlugsFactory,
        },
        {
          provide: RECOMMENDED_GROUP_CALENDAR_SLUGS_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: recommendedGroupCalendarSlugsFactory,
        },
        { provide: SETTINGS_LINK_TOKEN, deps: [MEETING_SCHEDULER_CONFIG_TOKEN], useFactory: settingsLinkFactory },
        {
          provide: INTEGRATIONS_PAGE_LINK_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: integrationLinkFactory,
        },
        {
          provide: CRM_ACTIVITY_FEED_PAGE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: crmActivityFeedLinkFactory,
        },
        {
          provide: CONTACT_PROFILE_PAGE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: contactProfileLinkFactory,
        },
        {
          provide: CALENDAR_PAGE_LINK_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: calendarPageLinkFactory,
        },
        {
          provide: CALENDAR_VIEW_PAGE_LINK_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: calendarViewPageLinkFactory,
        },
        { provide: EVENT_TYPE_PAGE_TOKEN, deps: [MEETING_SCHEDULER_CONFIG_TOKEN], useFactory: bookingLinkFactory },
        { provide: GROUP_PAGE_TOKEN, deps: [MEETING_SCHEDULER_CONFIG_TOKEN], useFactory: groupFactory },
        { provide: SERVICE_PAGE_TOKEN, deps: [MEETING_SCHEDULER_CONFIG_TOKEN], useFactory: serviceFactory },
        {
          provide: MEETING_LIST_PAGE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: meetingListLinkFactory,
        },
        {
          provide: MEETING_TYPE_LIST_VIEW_PAGE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: meetingTypeListViewLinkFactory,
        },
        {
          provide: MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: meetingSettingsListViewLinkFactory,
        },
        {
          provide: MEETING_SCHEDULER_MEETING_NAVIGATION_SERVICE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: meetingNavigationServiceFactory,
        },
        {
          provide: MEETING_INTEGRATION_SERVICE_KEYS_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: meetingIntegrationServiceKeysFactory,
        },
        {
          provide: INITIAL_AVAILABILITY_HOURS_TIMEOUT,
          useValue: 2000,
        },
        {
          provide: INITIAL_AVAILABILITY_HOURS,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN, ErrorHandler, INITIAL_AVAILABILITY_HOURS_TIMEOUT],
          useFactory: initialAvailabilityHoursFactory,
        },
        {
          provide: APPLICATION_CONTEXT_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: applicationContextFactory,
        },
        {
          provide: CONTEXT_DATA_TOKEN,
          deps: [APPLICATION_CONTEXT_TOKEN],
          useFactory: contextDataFactory,
        },
        {
          provide: PERSONAL_CALENDAR_ID_TOKEN,
          deps: [MeetingSchedulerStoreService],
          useFactory: primaryPersonalCalendarIdFactory,
        },
        {
          provide: ALTERNATE_TRANSLATIONS_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: alternateTranslationsFactory,
        },
        {
          provide: ICONS_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: iconsFactory,
        },

        {
          provide: FEATURE_MEETING_DATE_RANGE_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: featureDateRangeFactory,
        },
        {
          provide: FEATURE_MEETING_SESSION_EVENT_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: featureSessionEventFactory,
        },
        {
          provide: FEATURE_PRIORITY_ASSIGNMENT_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: featurePriorityAssignmentFactory,
        },
        {
          provide: FEATURE_SMS_ENABLED_TOKEN,
          deps: [MEETING_SCHEDULER_CONFIG_TOKEN],
          useFactory: featureSMSEnabledFactory,
        },
      ],
    };
    return result;
  }
}

export function userIdFactory(config: MeetingSchedulerConfig): Observable<string> {
  if (!config.userId$) {
    throw new Error('[MeetingScheduler]: userId$ not provided');
  }
  return config.userId$.pipe(distinctUntilChanged(), filter<string>(Boolean));
}

export function hostIdFactory(config: MeetingSchedulerConfig): Observable<string> {
  if (!config.hostId$) {
    throw new Error('[MeetingScheduler]: hostId$ not provided');
  }
  return config.hostId$.pipe(distinctUntilChanged(), filter<string>(Boolean));
}

export function personalCalendarSlugFactory(
  storeService: MeetingSchedulerStoreService,
): Observable<string | undefined> {
  return storeService.loadPersonalCalendar().pipe(
    map(function (personalCalendar): string {
      return personalCalendar.slug;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );
}

export function calendarServiceFactory(config: MeetingSchedulerConfig): MeetingSchedulerCalendarService {
  return config.calendarService || inject(StubMeetingSchedulerCalendarService);
}

export function meetingNavigationServiceFactory(config: MeetingSchedulerConfig): MeetingNavigationLinkService {
  return config.meetingNavigationLinkService || inject(StubMeetingNavigationLinkService);
}

export function recommendedPersonalCalendarSlugsFactory(config: MeetingSchedulerConfig): Observable<string[]> {
  return config.recommendedPersonalCalendarSlugs$ || of([]);
}

export function recommendedGroupCalendarSlugsFactory(
  config: MeetingSchedulerConfig,
): Observable<{ [externalCalendarId: string]: string[] }> {
  return config.recommendedGroupCalendarSlugs$ || of({});
}

export function settingsLinkFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'settingsPage');
}

export function integrationLinkFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'integrationsPage');
}

export function calendarPageLinkFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'calendarPage');
}

export function calendarViewPageLinkFactory(
  config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>,
): Observable<string> {
  return getCleanLinkStream$(config, 'calendarViewPage');
}

export function bookingLinkFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'eventTypePage');
}
export function groupFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'groupPage');
}
export function serviceFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'servicePage');
}

export function meetingListLinkFactory(config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>): Observable<string> {
  return getCleanLinkStream$(config, 'meetingListPage');
}

export function crmActivityFeedLinkFactory(
  config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>,
): Observable<string> {
  return getCleanLinkStream$(config, 'crmActivityFeedPage');
}

export function contactProfileLinkFactory(
  config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>,
): Observable<string> {
  return getCleanLinkStream$(config, 'contactProfilePage');
}

export function meetingTypeListViewLinkFactory(
  config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>,
): Observable<string> {
  return getCleanLinkStream$(config, 'meetingTypeListViewPage');
}

export function meetingSettingsListViewLinkFactory(
  config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>,
): Observable<string> {
  return getCleanLinkStream$(config, 'meetingSettingsListViewPage');
}

export function meetingIntegrationServiceKeysFactory(config: MeetingSchedulerConfig): MeetingIntegrationServiceKeys {
  return config.meetingIntegrationsServiceKeys;
}

export function useMeetingSchedulerNavigationBreadcrumbsFactory(config: MeetingSchedulerConfig): Observable<boolean> {
  const useBreadcrumbs$ = config.useBreadcrumbs$;
  if (typeof useBreadcrumbs$ === 'boolean') {
    return of(useBreadcrumbs$);
  }
  if (config.useBreadcrumbs$) {
    return useBreadcrumbs$;
  }
  return of(true);
}

export function initialAvailabilityHoursFactory(
  config: Pick<MeetingSchedulerConfig, 'initialAvailability$'>,
  errorHandler: Pick<ErrorHandler, 'handleError'>,
  timeoutMS: number,
  scheduler?: SchedulerLike,
): Observable<DailyAvailability> {
  if (!config.initialAvailability$) {
    return of(getDefaultAvailability());
  }
  return config.initialAvailability$.pipe(
    timeout(timeoutMS, scheduler),
    map(function (availability: DailyAvailability): DailyAvailability {
      return availability?.days?.size > 0 ? availability : getDefaultAvailability();
    }),
    catchError<DailyAvailability, Observable<DailyAvailability>>(function (err: Error): Observable<DailyAvailability> {
      errorHandler.handleError(err);
      return of(getDefaultAvailability());
    }),
  );
}

export function applicationContextFactory(config: MeetingSchedulerConfig): Observable<ApplicationContextPropertiesSet> {
  if (!config.applicationContext$) {
    throw new Error('Application Context is required for Meeting Scheduler');
  }
  return config.applicationContext$;
}

export function contextDataFactory(applicationContext$: Observable<ApplicationContextPropertiesSet>): Observable<ContextData> {
  return applicationContext$.pipe(
    filter(Boolean),
    map((context: ApplicationContextPropertiesSet) => {
      const contextObj = context as any;
      const userContext = contextObj.user_context;

      return {
        namespace: userContext === 'SMB' ? contextObj.business_id : contextObj.partner_id,
        type: userContext === 'SMB' ? Context.SMB : Context.PARTNER,
      };
    }),
    filter(data => Boolean(data.namespace)),
    shareReplay({ refCount: true, bufferSize: 1 })
  );
}

export function primaryPersonalCalendarIdFactory(storeService: MeetingSchedulerStoreService): Observable<string> {
  return storeService.personalCalendarId$;
}

export function featureDateRangeFactory(config: MeetingSchedulerConfig): Observable<boolean> {
  if (!config.featureDateRangeEnabled$) {
    return of(false);
  }
  return config.featureDateRangeEnabled$;
}

export function featureSessionEventFactory(config: MeetingSchedulerConfig): Observable<boolean> {
  if (!config.featureSessionEventEnabled$) {
    return of(false);
  }
  return config.featureSessionEventEnabled$;
}

export function featurePriorityAssignmentFactory(config: MeetingSchedulerConfig): Observable<boolean> {
  if (!config.featurePriorityAssignmentEnabled$) {
    return of(false);
  }
  return config.featurePriorityAssignmentEnabled$;
}

export function featureSMSEnabledFactory(config: MeetingSchedulerConfig): Observable<boolean> {
  if (!config.featureSMSEnabled$) {
    return of(false);
  }
  return config.featureSMSEnabled$;
}

export function alternateTranslationsFactory(config: MeetingSchedulerConfig): { [translationKey: string]: string } {
  return config.alternateTranslations || {};
}

export function iconsFactory(config: MeetingSchedulerConfig): MeetingSchedulerIcons {
  return config.icons;
}

function getCleanLinkStream$(
  config: Pick<MeetingSchedulerConfig, 'hostId$' | 'links'>,
  linkKey: keyof MeetingSchedulerNavigationLinks,
): Observable<string> {
  const linkVal$ = getObservableString(config.links[linkKey]);
  return combineLatest([config.hostId$.pipe(filter<string>(Boolean)), linkVal$]).pipe(
    map(function ([hostId, link]): string {
      return (link || '').replace('{{hostId}}', hostId);
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );
}
